import { useEffect, useRef, useState } from "react";
import { quantfolio, type QpApiCall } from "../api";
import { qpRail, useQpRailOpen } from "../lib/qpRail";
import { formatDateTime } from "../pages/crm/format";

// The right-side call-log rail: a live view of every Quantfolio Platform API
// call the app makes. Polls while open so calls stream in during a jump.
export function QpCallRail() {
  const open = useQpRailOpen();
  const [calls, setCalls] = useState<QpApiCall[]>([]);
  const [expanded, setExpanded] = useState<number | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!open) {
      if (timer.current) clearInterval(timer.current);
      timer.current = null;
      return;
    }
    let cancelled = false;
    const poll = () =>
      quantfolio
        .calls({ limit: 60 })
        .then((d) => !cancelled && setCalls(d.calls))
        .catch(() => {});
    poll();
    timer.current = setInterval(poll, 2000);
    return () => {
      cancelled = true;
      if (timer.current) clearInterval(timer.current);
    };
  }, [open]);

  async function clear() {
    try {
      await quantfolio.clearCalls();
      setCalls([]);
      setExpanded(null);
    } catch {
      /* ignore — next poll will re-sync */
    }
  }

  if (!open) return null;

  return (
    <aside className="qp-rail">
      <header className="qp-rail-head">
        <div>
          <span className="eyebrow">Quantfolio</span>
          <h2>API activity</h2>
        </div>
        <div className="qp-rail-actions">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={clear}
            disabled={calls.length === 0}
          >
            Clear
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => qpRail.close()}
            aria-label="Close activity rail"
          >
            Close
          </button>
        </div>
      </header>

      {calls.length === 0 ? (
        <p className="muted qp-rail-empty">
          No calls yet. Jump into Quantfolio to see the integration work.
        </p>
      ) : (
        <ul className="qp-call-list">
          {calls.map((c) => (
            <li key={c.id} className="qp-call">
              <button
                type="button"
                className="qp-call-row"
                onClick={() => setExpanded(expanded === c.id ? null : c.id)}
              >
                <span className={`pill qp-method qp-method-${c.method.toLowerCase()}`}>
                  {c.method}
                </span>
                <span className="qp-path" title={c.path}>
                  {stripQuery(c.path)}
                </span>
                <span
                  className={`pill qp-status ${
                    c.ok ? "status-executed" : "status-rejected"
                  }`}
                >
                  {c.status ?? "ERR"}
                </span>
                <span className="qp-dur muted">
                  {c.duration_ms != null ? `${c.duration_ms}ms` : ""}
                </span>
              </button>
              {expanded === c.id && (
                <div className="qp-call-detail">
                  <div className="qp-call-meta muted">
                    {formatDateTime(c.created_at)}
                    {c.jump_id != null ? ` · jump #${c.jump_id}` : ""}
                    {" · "}
                    <span className="mono">{c.path}</span>
                  </div>
                  {c.error && <div className="banner-error">{c.error}</div>}
                  {c.request_json && (
                    <Json label="Request" text={c.request_json} />
                  )}
                  {c.response_json && (
                    <Json label="Response" text={c.response_json} />
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}

function stripQuery(path: string): string {
  const i = path.indexOf("?");
  return i === -1 ? path : path.slice(0, i);
}

function Json({ label, text }: { label: string; text: string }) {
  return (
    <div className="qp-json">
      <span className="qp-json-label muted">{label}</span>
      <pre>
        <code>{pretty(text)}</code>
      </pre>
    </div>
  );
}

function pretty(text: string): string {
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
}
